import React, { useState, useRef, useEffect } from 'react';
import { Send, Hash, X, Loader2, Image as ImageIcon, AlertCircle } from 'lucide-react';
import { useCreateDiscussion } from '../hooks/use-panels';
import { uploadFile, confirmUpload } from '../../../lib/uploads';

const CreateDiscussionForm = ({ panelId, isExpanded: controlledIsExpanded, setIsExpanded: controlledSetIsExpanded }) => {
  const [internalIsExpanded, setInternalIsExpanded] = useState(false);
  const isExpanded = controlledIsExpanded !== undefined ? controlledIsExpanded : internalIsExpanded;
  const setIsExpanded = controlledSetIsExpanded || setInternalIsExpanded;

  const [form, setForm] = useState({
    title: '',
    content: '',
    tagInput: '',
    tags: [],
    images: [],       // storage keys already saved (edit mode)
    // Uploads in progress or finished, one per newly chosen file:
    // { file, key, uploading, progress, failed }. Each starts uploading as soon
    // as it is picked, so `key` fills in while the rest of the form is written.
    pendingFiles: [],
    previews: [],     // thumbnails: blob: for pending, signed URL for stored
  });
  const [error, setError] = useState('');
  const [showTagInput, setShowTagInput] = useState(false);
  const [showImageTooltip, setShowImageTooltip] = useState(false);
  // Spans the whole submit, not just the mutation. Promoting the images out of
  // `tmp/` is a round-trip per image before `createDiscussion` even starts, so
  // the mutation's own isPending leaves the button silent through all of it.
  const [isPosting, setIsPosting] = useState(false);

  const fileInputRef = useRef(null);
  const titleRef = useRef(null);
  const contentRef = useRef(null);

  // Auto-resize title textarea as text is added or removed
  useEffect(() => {
    if (titleRef.current) {
      titleRef.current.style.height = 'auto';
      titleRef.current.style.height = `${Math.max(42, titleRef.current.scrollHeight)}px`;
    }
  }, [form.title, isExpanded]);

  // Auto-resize content textarea as text is added or removed
  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.style.height = 'auto';
      contentRef.current.style.height = `${Math.max(110, contentRef.current.scrollHeight)}px`;
    }
  }, [form.content, isExpanded]);

  const isUploading = (form.pendingFiles || []).some((p) => p.uploading);

  const { mutate: createDiscussion, isPending: isCreating } = useCreateDiscussion();

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      const tag = form.tagInput.trim().replace(/^#/, '');
      if (tag && !form.tags.includes(tag)) {
        setForm((prev) => ({ ...prev, tags: [...prev.tags, tag], tagInput: '' }));
      }
    }
  };

  const removeTag = (tag) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  };

  const removeImage = (indexToRemove) => {
    setForm(prev => {
      // previews is [...already-stored images, ...newly picked files], so an
      // index past the stored ones refers to a pending file.
      const storedCount = prev.images.length;
      const url = (prev.previews || [])[indexToRemove];
      if (url?.startsWith('blob:')) URL.revokeObjectURL(url);
      return {
        ...prev,
        images: prev.images.filter((_, i) => i !== indexToRemove),
        pendingFiles: (prev.pendingFiles || []).filter(
          (_, i) => i + storedCount !== indexToRemove
        ),
        previews: (prev.previews || []).filter((_, i) => i !== indexToRemove),
      };
    });
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    if (form.images.length + (form.pendingFiles?.length || 0) + files.length > 2) {
      setError('You can upload a maximum of 2 images.');
      return;
    }

    for (let file of files) {
      if (!file.type.startsWith('image/')) {
        setError('Please select only image files (JPG, PNG, GIF)');
        return;
      }
      // Enforce 1MB limit per image
      if (file.size > 1 * 1024 * 1024) {
        setError('Image must be under 1MB.');
        return;
      }
    }

    setError('');
    if (fileInputRef.current) fileInputRef.current.value = '';

    // Show each file immediately from a local blob:, then upload it in the
    // background. Until an upload is confirmed the object lives only in a
    // staging prefix the bucket sweeps, so abandoning the form costs nothing
    // that lasts.
    setForm((prev) => ({
      ...prev,
      pendingFiles: [
        ...(prev.pendingFiles || []),
        ...files.map((file) => ({ file, key: null, uploading: true, progress: 0, failed: false })),
      ],
      previews: [...(prev.previews || []), ...files.map((f) => URL.createObjectURL(f))],
    }));

    // Patch by identity, not index: several uploads finish in any order, and
    // removing a thumbnail mid-flight shifts every index after it.
    const patch = (file, changes) =>
      setForm((prev) => ({
        ...prev,
        pendingFiles: (prev.pendingFiles || []).map((p) =>
          p.file === file ? { ...p, ...changes } : p
        ),
      }));

    await Promise.all(
      files.map(async (file) => {
        try {
          const result = await uploadFile(file, {
            purpose: 'discussion-image',
            onProgress: (progress) => patch(file, { progress }),
          });
          patch(file, { key: result.key, uploading: false, progress: 100 });
        } catch (err) {
          console.error('Upload error:', err);
          patch(file, { uploading: false, failed: true });
          setError(
            err?.response?.data?.message || 'An image failed to upload. Please remove it and try again.'
          );
        }
      })
    );
  };

  const handleCancel = () => {
    setIsExpanded(false);
    setForm({ title: '', content: '', tagInput: '', tags: [], images: [], pendingFiles: [], previews: [] });
    setShowTagInput(false);
    setError('');
    if (titleRef.current) {
      titleRef.current.style.height = '42px';
    }
    if (contentRef.current) {
      contentRef.current.style.height = '110px';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Validate before uploading, so a missing title costs no stored object.
    if (!form.title.trim() || !form.content.trim()) {
      setError('Title and content are required.');
      return;
    }

    // The images are already in B2 by now — only wait if any are still going.
    if (isUploading) {
      setError('Please wait for the images to finish uploading.');
      return;
    }
    if ((form.pendingFiles || []).some((p) => p.failed)) {
      setError('An image failed to upload. Please remove it and try again.');
      return;
    }

    // Promote the staged uploads. Every image sits under `tmp/` until this
    // point, where the bucket lifecycle rule sweeps it — so abandoning this form
    // leaves nothing stranded at a permanent key. See lib/uploads.js.
    //
    // This is also where a file whose bytes do not match its declared type is
    // rejected, so a failure here must stop the submit: the object is deleted
    // server-side and must not be referenced.
    const staged = (form.pendingFiles || []).filter((p) => p.key);
    let promoted;
    setIsPosting(true);
    try {
      promoted = await Promise.all(
        staged.map(async (p) => ({
          ...p,
          key: (await confirmUpload(p.key, 'discussion-image')).key,
        }))
      );
    } catch (err) {
      console.error('Confirm error:', err);
      // Split the same way putToStorage splits its retries. A 4xx is the file
      // itself being refused — wrong type, too large, not this user's — and
      // removing it is the real fix. Anything else (5xx, or no response at all
      // when the gateway has no upstream mid-deploy) is transient: the image is
      // valid and still staged, so telling the user to remove it would throw
      // away a good upload to fix a problem it did not cause.
      const status = err?.response?.status;
      setError(
        status && status < 500
          ? err?.response?.data?.message ||
          'An image could not be saved. Please remove it and try again.'
          : 'Could not reach the server. Your images are still attached — please try again.'
      );
      setIsPosting(false);
      return;
    }

    // Write the permanent keys back, so a retry after a failed save does not try
    // to confirm the same staging key a second time.
    setForm((prev) => ({
      ...prev,
      pendingFiles: (prev.pendingFiles || []).map(
        (p) => promoted.find((q) => q.file === p.file) ?? p
      ),
    }));

    const imageKeys = [...form.images, ...promoted.map((p) => p.key)];

    const extractedTags = (form.content.match(/#[\w-]+/g) || []).map(t => t.slice(1));
    const pendingTag = form.tagInput.trim().replace(/^#/, '');
    const allTags = [...new Set([...form.tags, ...extractedTags, ...(pendingTag ? [pendingTag] : [])])];

    const payload = {
      panelId,
      title: form.title,
      content: form.content,
      tags: allTags,
      images: imageKeys?.length > 0 ? imageKeys : undefined
    };

    createDiscussion(payload, {
      onSuccess: () => {
        setIsPosting(false);
        handleCancel();
      },
      onError: (err) => {
        setIsPosting(false);
        setError(err?.response?.data?.message || 'Failed to create discussion.');
      },
    });
  };

  if (!isExpanded) {
    return null;
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 mb-6 shadow-sm animate-fadeIn">
      <h2 className="text-sm font-semibold text-gray-900 mb-4">Start a New Discussion</h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="relative">
          <textarea
            ref={titleRef}
            rows={1}
            value={form.title}
            maxLength={300}
            onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }}
            onChange={(e) => handleChange('title', e.target.value)}
            placeholder="Discussion title (max 300 characters)"
            className="w-full px-4 py-2.5 pr-20 text-sm bg-gray-50 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#6b46c1] focus:border-transparent transition-colors break-words [overflow-wrap:anywhere] overflow-y-hidden resize-none min-h-[42px]"
            autoFocus
          />
          <div className="absolute right-3 top-3 text-xs text-gray-400 pointer-events-none">
            <span className={form.title.length >= 300 ? "text-red-500 font-bold" : form.title.length >= 270 ? "text-amber-500 font-semibold" : ""}>
              {form.title.length} / 300
            </span>
          </div>
        </div>

        <div className="relative">
          <textarea
            ref={contentRef}
            value={form.content}
            maxLength={5000}
            onChange={(e) => handleChange('content', e.target.value)}
            placeholder="Share your insights, ask questions, or start a clinical discussion..."
            rows={4}
            className="w-full px-4 py-3 pb-9 text-sm bg-gray-50 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#6b46c1] focus:border-transparent transition-colors break-words [overflow-wrap:anywhere] overflow-y-hidden resize-none min-h-[110px]"
          />
          <div className="absolute bottom-3 left-4 text-xs text-gray-400 pointer-events-none">
            <span className={form.content.length >= 5000 ? "text-red-500 font-bold" : form.content.length >= 4500 ? "text-amber-500 font-semibold" : ""}>
              {form.content.length}
            </span> / 5000 characters
          </div>
        </div>

        {/* Image Guidelines Card (Case Share Style) */}
        <div className="flex items-start gap-2.5 p-3 bg-indigo-50/60 border border-indigo-100/80 rounded-xl text-xs text-indigo-900">
          <ImageIcon size={15} className="text-indigo-600 shrink-0 mt-0.5" />
          <div className="space-y-0.5 text-[11px] leading-relaxed">
            <span className="font-semibold text-indigo-950 block text-xs">Image Upload Guidelines</span>
            <p className="text-gray-600">Supports JPG, PNG, GIF, WebP • Max 1 MB per image • Up to 2 images</p>
          </div>
        </div>

        {/* Gate on previews, not images: a file picked but not yet uploaded has
            a preview and no key, so keying off images hides it until submit. */}
        {form.previews && form.previews.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-2">
            {(form.previews || []).map((url, index) => {
              // previews is [...already-stored, ...newly picked], so anything
              // past the stored ones maps to a pending upload slot.
              const slot = (form.pendingFiles || [])[index - form.images.length];
              return (
                <div key={url} className="relative inline-block">
                  <img
                    src={url}
                    alt={`Upload preview ${index + 1}`}
                    className={`h-24 w-24 sm:h-32 sm:w-32 rounded-lg object-cover border ${slot?.failed ? 'border-red-300' : 'border-gray-200'
                      }`}
                  />
                  {slot?.uploading && (
                    <div className="absolute inset-0 bg-black/40 rounded-lg flex flex-col items-center justify-center text-white">
                      <Loader2 className="animate-spin" size={18} />
                      <span className="text-[11px] font-medium mt-1">{slot.progress}%</span>
                    </div>
                  )}
                  {slot?.failed && (
                    <div className="absolute inset-0 bg-red-900/50 rounded-lg flex items-center justify-center">
                      <span className="text-[11px] font-semibold text-white px-1 text-center">Upload failed</span>
                    </div>
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 p-1 bg-white border border-gray-200 rounded-full text-gray-500 hover:text-red-500 hover:bg-red-50 shadow-sm"
                  >
                    <X size={14} />
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {showTagInput && (
          <div className="pt-2 animate-fadeIn">
            <div className="flex flex-wrap gap-2 mb-2">
              {form.tags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-600 text-xs font-medium rounded-md"
                >
                  <Hash size={11} />
                  {tag}
                  <button type="button" onClick={() => removeTag(tag)} className="hover:text-blue-800">
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
            <input
              type="text"
              value={form.tagInput}
              onChange={(e) => handleChange('tagInput', e.target.value)}
              onKeyDown={handleAddTag}
              placeholder="Add tags (press Enter to add)"
              className="w-full sm:w-1/2 px-4 py-2.5 text-sm bg-gray-50 border-transparent rounded-lg focus:bg-white focus:ring-2 focus:ring-[#6b46c1] focus:border-transparent transition-colors"
            />
          </div>
        )}

        {error && (
          <p className="text-sm text-red-600">{error}</p>
        )}

        <div className="flex items-center justify-between pt-2">
          <div className="flex items-center gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileUpload}
              className="hidden"
              accept="image/*"
              multiple
            />
            <div className="relative inline-block">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                onMouseEnter={() => setShowImageTooltip(true)}
                onMouseLeave={() => setShowImageTooltip(false)}
                disabled={isUploading || form.images.length + (form.pendingFiles?.length || 0) >= 2}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                <ImageIcon size={13} className="text-indigo-600" />
                <span>+Add Image</span>
                {form.images.length + (form.pendingFiles?.length || 0) > 0 && (
                  <span className="text-[10px] bg-indigo-100 text-indigo-800 px-1.5 py-0.2 rounded-full font-bold">
                    {form.images.length + (form.pendingFiles?.length || 0)}/2
                  </span>
                )}
              </button>

              {/* Image Formats & Limits Tooltip Popover (Case Share Style) */}
              {showImageTooltip && (
                <div className="absolute left-0 bottom-full mb-2 z-50 w-52 p-2.5 bg-gray-900 text-white rounded-xl shadow-2xl text-xs space-y-1.5 pointer-events-none border border-gray-700/60 animate-in fade-in zoom-in-95">
                  <div className="flex items-center gap-1.5 font-bold text-indigo-300 text-xs border-b border-gray-700 pb-1">
                    <ImageIcon size={13} />
                    <span>Allowed Image Formats</span>
                  </div>
                  <div className="space-y-1 text-[11px] text-gray-200 pt-0.5">
                    <p><span className="font-semibold text-white">• Supported: </span>JPG, JPEG, PNG, WebP, GIF</p>
                    <p><span className="font-semibold text-white">• Limit: </span>Max 2 images</p>
                    <p><span className="font-semibold text-white">• Max Size: </span>1 MB per image</p>
                  </div>
                  <div className="mt-1 pt-1 border-t border-gray-700 text-gray-300 text-[10px] font-medium flex items-center justify-between">
                    <span>Attached: {form.images.length + (form.pendingFiles?.length || 0)}/2</span>
                    {form.images.length + (form.pendingFiles?.length || 0) >= 2 && (
                      <span className="text-amber-400 font-semibold">Max reached</span>
                    )}
                  </div>
                </div>
              )}
            </div>
            <button
              type="button"
              onClick={() => setShowTagInput(!showTagInput)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
            >
              Add Tags
            </button>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="text-sm font-medium text-gray-500 hover:text-gray-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPosting || isCreating || isUploading || form.title.length < 5 || form.content.length < 10}
              className="flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-[#6b46c1] rounded-lg hover:bg-[#553098] transition-colors disabled:opacity-60 shadow-sm"
            >
              {/* One spinner for the whole submit — promoting the images out of
                  `tmp/` and then saving the row. It deliberately does NOT cover
                  the upload phase: the thumbnails carry their own spinner and
                  percentage there, and a second spinner would read as two
                  separate things in flight. The button stays disabled until the
                  uploads finish. */}
              {isPosting || isCreating ? <Loader2 size={16} className="animate-spin" /> : <Send size={14} />}
              {isPosting || isCreating ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateDiscussionForm;
